/**
 * SEO meta tag helpers for React Router routes.
 *
 * @see {@link file://app/lib/brand/config.ts} — default SEO configuration
 */
import type { MetaDescriptor, MetaFunction } from "react-router"

import { seoConfig, siteConfig } from "@/lib/brand/config"

/** Return the application-wide default SEO config. */
export const getDefaultSeoTags = () => {
  return seoConfig
}

/**
 * The address search engines should list a page under.
 *
 * Always on production, also when beta renders the page, so a copy of a page
 * points at the original. The query string is left out: it never changes
 * what a public page shows, and each variant would otherwise count as a page
 * of its own. A trailing slash is left out for the same reason.
 *
 * The path is appended rather than resolved with `new URL`, which would read
 * a path starting `//` as another host.
 */
export const getCanonicalUrl = (pathname: string) => {
  const path = pathname.replace(/\/+$/, "")
  return `${siteConfig.baseUrl}${path || "/"}`
}

/**
 * Add the link preview's title and description to a page's own meta.
 *
 * A shared link shows `og:title` and `og:description`, not the page's title
 * and description. Without this, every page previewed as the site's defaults
 * from the root. A page that names a preview text of its own keeps it.
 */
const withLinkPreview = (overrides: MetaDescriptor[]): MetaDescriptor[] => {
  const names = (property: string) =>
    overrides.some((meta) => "property" in meta && meta.property === property)
  const preview: MetaDescriptor[] = []

  for (const meta of overrides) {
    if ("title" in meta && !names("og:title")) {
      preview.push({ property: "og:title", content: meta.title })
    }
    if (
      "name" in meta &&
      meta.name === "description" &&
      !names("og:description")
    ) {
      preview.push({ property: "og:description", content: meta.content })
    }
  }

  return [...overrides, ...preview]
}

/**
 * Create a `MetaFunction` that merges parent route meta with overrides.
 *
 * Strategy: walks parent matches in reverse, collects meta descriptors,
 * then replaces any that share the same `name`, `property`, or `title`
 * with the overrides. An override the parent has no tag for is added.
 * A page's title and description also become its link preview's (see
 * `withLinkPreview`). Optionally appends additional meta from `appendFn`.
 *
 * Adapted from: https://gist.github.com/ryanflorence/ec1849c6d690cfbffcb408ecd633e069
 */
export const mergeMeta = (
  overrideFn: MetaFunction,
  appendFn?: MetaFunction
): MetaFunction => {
  return (arg) => {
    // get meta from parent routes
    let mergedMeta = arg.matches.reduceRight((acc, match) => {
      if (match.meta.length > 0 && acc.length == 0) {
        return acc.concat(match.meta || [])
      }
      return acc
    }, [] as MetaDescriptor[])

    // replace any parent meta with the same name or property with the override
    const overrides = overrideFn(arg)
    if (overrides) {
      for (const override of withLinkPreview(overrides)) {
        const index = mergedMeta.findIndex(
          (meta) =>
            ("name" in meta &&
              "name" in override &&
              meta.name === override.name) ||
            ("property" in meta &&
              "property" in override &&
              meta.property === override.property) ||
            ("title" in meta && "title" in override)
        )
        if (index === -1) {
          mergedMeta.push(override)
        } else {
          mergedMeta.splice(index, 1, override)
        }
      }
    }

    // append any additional meta
    if (appendFn) {
      const appendMeta = appendFn(arg)
      if (appendMeta) {
        mergedMeta = mergedMeta.concat(appendMeta)
      }
    }

    return mergedMeta
  }
}
